import { useState } from "react";
import { PhoneCall, ListOrdered, ShieldAlert, Users, Settings2, BarChart3, Radio, Wallet, Mic } from "lucide-react";
import { useAuth } from "@/context/CrmAuth";
import { cn } from "@/lib/utils";
import Simulator from "@/crm/components/telephony/Simulator";
import CallJournal from "@/crm/components/telephony/CallJournal";
import FlaggedNumbers from "@/crm/components/telephony/FlaggedNumbers";
import TeamAvailability from "@/crm/components/telephony/TeamAvailability";
import RoutingSettings from "@/crm/components/telephony/RoutingSettings";
import TelephonyStats from "@/crm/components/telephony/TelephonyStats";
import ProviderSettings from "@/crm/components/telephony/ProviderSettings";
import TranscriptionSettings from "@/crm/components/telephony/TranscriptionSettings";
import TelephonyCosts from "@/crm/components/telephony/TelephonyCosts";

const TABS = [
  { key: "communications", label: "Communications", icon: BarChart3 },
  { key: "simulateur", label: "Simulateur d'appel", icon: PhoneCall, roles: ["admin"] },
  { key: "journal", label: "Journal des appels", icon: ListOrdered },
  { key: "numeros", label: "Numéros signalés", icon: ShieldAlert },
  { key: "equipe", label: "Équipe & disponibilité", icon: Users, roles: ["admin"] },
  { key: "parametres", label: "Routage & groupes", icon: Settings2, roles: ["admin", "manager"] },
  { key: "fournisseur", label: "Fournisseur", icon: Radio, roles: ["admin", "manager"] },
  { key: "transcription", label: "Transcription IA", icon: Mic, roles: ["admin", "manager"] },
  { key: "budget", label: "Budget & coûts", icon: Wallet, roles: ["admin"] },
];

const VIEWS = {
  communications: TelephonyStats,
  simulateur: Simulator,
  journal: CallJournal,
  numeros: FlaggedNumbers,
  equipe: TeamAvailability,
  parametres: RoutingSettings,
  fournisseur: ProviderSettings,
  transcription: TranscriptionSettings,
  budget: TelephonyCosts,
};

export default function Telephony() {
  const { user } = useAuth();
  const [tab, setTab] = useState("communications");
  const tabs = TABS.filter((t) => !t.roles || t.roles.includes(user?.role));
  // On ne rend que des vues effectivement ouvertes au rôle : masquer l'onglet
  // ne doit pas laisser la vue accessible par un état résiduel.
  const allowed = tabs.some((t) => t.key === tab);
  const View = (allowed && VIEWS[tab]) || TelephonyStats;

  return (
    <div data-testid="telephony-page">
      {/* En-tête : titre + pastille d'état du routage (remplace PageHeader sur cette page) */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="max-w-xl">
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Téléphonie</h1>
          <p className="mt-2 text-sm text-slate-500 text-pretty">
            Le CRM décide automatiquement qui reçoit chaque appel entrant, selon les règles du manager.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-3 pr-3.5 py-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          <span className="text-sm text-slate-600">Routage actif</span>
        </div>
      </div>

      {/* Rail vertical en desktop, barre défilante en mobile */}
      <div className="grid gap-6 lg:grid-cols-[236px_minmax(0,1fr)] items-start">
        <nav
          className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0 lg:sticky lg:top-5"
          aria-label="Sections téléphonie"
        >
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`tel-tab-${t.key}`}
              aria-current={tab === t.key ? "page" : undefined}
              onClick={() => setTab(t.key)}
              className={cn(
                "flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors",
                tab === t.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              )}
            >
              <t.icon size={16} className={tab === t.key ? "text-white" : "text-slate-400"} />
              <span className="flex-1 text-left">{t.label}</span>
              {t.roles && (
                <span
                  className={cn(
                    "hidden lg:inline rounded border px-1 text-[9.5px] font-mono uppercase tracking-wider",
                    tab === t.key ? "border-white/30 text-white/70" : "border-slate-200 text-slate-400"
                  )}
                >
                  admin
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="min-w-0">
          <View />
        </div>
      </div>
    </div>
  );
}
